import { z } from 'zod';

import { apiGet, apiPost } from '../client';
import { CategorySchema, EmptyResponseSchema, NeighborhoodSchema } from '../schemas';
import type { SignupCountry } from '../../lib/country';
import type { BillingCycle, PlanTier } from '../../lib/plan';

/** Alta propia (services/api: business-signup-routes.ts). */

export const SignupResponseSchema = z.object({
  data: z.object({
    organizationId: z.string(),
    businessId: z.string(),
    /** What the person will type to sign in; derived from their email. */
    username: z.string(),
  }),
});
export type SignupResponse = z.infer<typeof SignupResponseSchema>;

export const AuthMethodsResponseSchema = z.object({
  data: z.object({
    password: z.boolean(),
    signup: z.boolean(),
    google: z.boolean(),
    // Optional: an API from before Entrar con WhatsApp simply does not offer it.
    whatsapp: z.boolean().optional(),
  }),
});

export type SignupInput = {
  organizationName: string;
  businessName: string;
  category: z.infer<typeof CategorySchema>;
  /** A Dale zone in Panamá and Venezuela; anywhere else the business's own city or area. */
  neighborhood: z.infer<typeof NeighborhoodSchema> | string;
  ownerName: string;
  email: string;
  password: string;
  address?: string;
  contactPhoneE164?: string;
  lat?: number;
  lng?: number;
  hours?: { dayOfWeek: number; opensAt?: string; closesAt?: string; isClosed?: boolean }[];
  /** Picked on the signup page; stored with the business in the same write. */
  plan?: PlanTier;
  billingCycle?: BillingCycle;
  /** ISO code, any country since 2 Nov 2026; Panamá and Venezuela keep their own clock. Absent is Panamá. */
  country?: SignupCountry;
  /** The device clock (IANA), sent only outside Panamá and Venezuela. */
  timeZone?: string;
  /** The password of Martín's invitation link: three months free on any plan. */
  inviteCode?: string;
};

export function signup(input: SignupInput, idempotencyKey: string): Promise<SignupResponse> {
  return apiPost('/v1/business/signup', SignupResponseSchema, input, idempotencyKey);
}

/** Checks the invitation password before the form: 204 when it opens, 403 when it does not, 429 after too many tries. */
export function checkInvite(code: string): Promise<undefined> {
  return apiPost('/v1/business/signup/invite', EmptyResponseSchema, { code });
}

/** Which doors this deployment has open: password, self-serve signup, Google. */
export function getAuthMethods(): Promise<{
  data: { password: boolean; signup: boolean; google: boolean; whatsapp?: boolean };
}> {
  return apiGet('/v1/business/auth/methods', AuthMethodsResponseSchema);
}

/**
 * Google sign-in is a full-page redirect, not a fetch: the browser has to
 * visit Google and come back with a cookie, which XHR cannot do.
 */
export function startGoogleAuth(flow: 'login' | 'link'): void {
  window.location.href = `/v1/business/auth/google/start?flow=${flow}`;
}

// ---------------------------------------------------------------------------
// Importar desde la página web del negocio, Google, Degusta u OpenTable
// ---------------------------------------------------------------------------

export const ImportPreviewResponseSchema = z.object({
  data: z.object({
    fields: z.object({
      name: z.string().optional(),
      address: z.string().optional(),
      phone: z.string().optional(),
      lat: z.number().optional(),
      lng: z.number().optional(),
      category: z.string().optional(),
      neighborhood: z.string().optional(),
      hours: z
        .array(z.object({ dayOfWeek: z.number(), opensAt: z.string(), closesAt: z.string() }))
        .optional(),
      // From the owner's own website (the crawler); the signup does not store these yet.
      website: z.string().optional(),
      email: z.string().optional(),
      whatsapp: z.string().optional(),
      instagram: z.string().optional(),
      facebook: z.string().optional(),
      tiktok: z.string().optional(),
      logoUrl: z.string().optional(),
      description: z.string().optional(),
      services: z.array(z.object({ name: z.string(), priceCents: z.number().optional(), durationMin: z.number().optional() })).optional(),
    }),
    source: z.string().optional(),
    warnings: z.array(z.string()),
  }),
});
export type ImportPreview = z.infer<typeof ImportPreviewResponseSchema>['data'];

/** Suggestions only: the wizard shows every field editable and the owner confirms. */
export function importPreview(input: { url?: string; text?: string }): Promise<{ data: ImportPreview }> {
  return apiPost('/v1/business/import/preview', ImportPreviewResponseSchema, input);
}
